<template>
  <Combobox
    as="div"
    :modelValue="selectedOption"
    @update:modelValue="selectedOption = $event"
    class="w-100 position-relative"
    by="value"
    v-slot="{ open: isComboboxOpen }"
    nullable
    :disabled="disabled"
  >
    <Float
      as="div"
      placement="bottom-start"
      :offset="5"
      flip
      shift
      adaptiveWidth
      portal
      enter="transition duration-200 ease-out"
      enter-from="opacity-0"
      enter-to="opacity-100"
      leave="transition duration-75 ease-in"
      leave-from="opacity-100"
      leave-to="opacity-0"
      :show="isComboboxOpen"
    >
      <div class="position-relative input-group">
        <ComboboxInput
          ref="comboboxInput"
          as="input"
          type="text"
          class="form-control pe-4"
          :class="{
            'form-control-sm': size === 'sm',
            'is-invalid': invalid,
          }"
          @input="handleInputChange"
          :displayValue="displayValueFn"
          :placeholder="placeholder"
          @focus="handleInputFocus"
        />
        <slot name="trailing" />
        <ComboboxButton
          ref="comboboxButton"
          class="input-group-text"
          @click="handleComboboxButtonClick"
        >
          <FontAwesomeIcon :icon="triggerIcon" />
        </ComboboxButton>
      </div>

      <ComboboxOptions
        class="w-100 overflow-x-hidden overflow-y-auto text-break search-results-height bg-white border rounded-2 shadow-sm p-0"
        :style="{ maxHeight: maxHeight ?? '300px', zIndex: 9999 }"
        static
      >
        <div
          v-if="isLoading"
          class="px-3 py-2 text-muted d-flex align-items-center justify-content-center"
        >
          <LoadingIndicatorComponent inline :message="$t('common.loading')" />
        </div>
        <div
          v-if="isError"
          class="px-3 py-2 text-muted d-flex align-items-center justify-content-center"
        >
          <FontAwesomeIcon icon="fas fa-exclamation-triangle" />
          <span>{{ errorMessage ?? $t("common.unexpected_error") }}</span>
        </div>
        <div
          v-else-if="
            isComboboxOpen && (filteredOptions.length > 0 || searchQuery)
          "
        >
          <ComboboxOption
            v-for="option in visibleOptions"
            :key="option.value"
            :value="option"
            as="template"
            #default="{ active, selected }"
          >
            <li
              class="search-result-item d-flex align-items-center px-3 py-2 cursor-pointer"
              :class="{ 'bg-light': active }"
              tabindex="-1"
            >
              <div class="flex-grow-1 text-truncate">
                <WordHighlighter
                  :query="highlightQuery"
                  :textToHighlight="option.label"
                  highlightClass="text-primary p-0"
                  wrapperTag="div"
                  wrapperClass="fw-medium text-wrap"
                />
                <slot
                  name="description"
                  :option="option"
                  :query="highlightQuery"
                >
                  <WordHighlighter
                    v-if="option.description"
                    :query="highlightQuery"
                    :textToHighlight="option.description"
                    highlightClass="text-primary p-0"
                    wrapperTag="div"
                    wrapperClass="text-muted small text-truncate"
                  />
                </slot>
              </div>
              <span v-if="selected" class="ms-2 text-primary">
                <font-awesome-icon icon="fas fa-check" />
              </span>
            </li>
          </ComboboxOption>
          <div
            v-if="hiddenMatchCount > 0"
            class="px-3 py-2 text-muted small border-top"
          >
            {{
              $t("common.partial_results", {
                shown: visibleOptions.length,
                total: filteredOptions.length,
              })
            }}
          </div>
          <div
            v-if="filteredOptions.length === 0 && searchQuery"
            class="px-3 py-2 text-muted"
          >
            {{ $t("common.no_results") }}
          </div>
        </div>
      </ComboboxOptions>
    </Float>
  </Combobox>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { Float } from "@headlessui-float/vue";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/vue";
import { computed, onBeforeUnmount, ref, useTemplateRef, watch } from "vue";
import WordHighlighter from "vue-word-highlighter";

// PERF: Headless UI re-sorts every registered option against its siblings' DOM position each time
// one of them mounts, so an open dropdown costs O(n^2) in the number of options rendered. Measured
// on /admin/portfolios, whose company list is ~2000 entries: 450 options took 200ms, 2000 took
// 5.2s — long enough that a second keystroke landed on a frozen page. Rendering only a window
// keeps the cost flat; the notice under the list asks the user to narrow the search.
//
// The window sits above every list a caller hands over whole and expects to browse by scrolling —
// the 251-entry country list behind CountryPickerComponent and PhoneNumberInput (250 names plus the
// "Other" sentinel) and the 200-suggestion cap `beneficiary_suggestions_service` publishes — so
// only the company searches, which are typed into rather than scrolled, ever reach it. Raise it if
// the country list grows. Options past the cut are not lost either way: the search filters the
// whole option list rather than the window, so refining the query still reaches them.
const MAX_RENDERED_OPTIONS = 300;

interface Option {
  label: string;
  value: any;
  description?: string;
}

const props = withDefaults(
  defineProps<{
    options: Option[];
    modelValue?: any;
    placeholder?: string;
    size?: "sm" | "md";
    isLoading?: boolean;
    isError?: boolean;
    /** Shown while `isError` is set, in place of the generic "unexpected error" text. */
    errorMessage?: string;
    clearOnFocus?: boolean;
    autoOpen?: boolean;
    keepOpenOnSelect?: boolean;
    maxHeight?: string;
    invalid?: boolean;
    triggerIcon?: string;
    allowFreeText?: boolean;
    disabled?: boolean;
  }>(),
  { triggerIcon: "fas fa-magnifying-glass" }
);

const emit = defineEmits<{
  "update:modelValue": [value: any];
  queryUpdated: [value: string];
}>();

const searchQuery = ref("");
const comboboxButton = useTemplateRef<any>("comboboxButton");
const comboboxInput = useTemplateRef<any>("comboboxInput");
// Only relevant when autoOpen is on: Headless UI refocuses the input after
// selection, and that focus event would otherwise reopen the dropdown via
// autoOpen. The flag lets handleInputFocus swallow that single reopen, and
// the nested RAF blurs the input afterwards so the suppression doesn't
// leak (e.g. keyboard select fires no focus event).
const suppressNextAutoOpen = ref(false);
const postSelectRafId = ref<number | null>(null);

const cancelPostSelectRaf = () => {
  if (postSelectRafId.value !== null) {
    cancelAnimationFrame(postSelectRafId.value);
    postSelectRafId.value = null;
  }
};

onBeforeUnmount(cancelPostSelectRaf);

const selectedOption = computed<Option | undefined>({
  get() {
    const matchingOption = props.options.find(
      (opt) => opt.value === props.modelValue
    );
    if (matchingOption || !props.allowFreeText || !props.modelValue) {
      return matchingOption;
    }
    // Headless UI skips `displayValue` entirely while the Combobox value is undefined, so free text
    // held by the model would render as an empty field. Standing it up as its own option is what
    // makes it visible; it is the Combobox's value only, never a row in the dropdown.
    return { label: String(props.modelValue), value: props.modelValue };
  },
  set(option?: Option) {
    emit("update:modelValue", option?.value);
    searchQuery.value = option ? option.label : "";

    if (option && props.autoOpen && !props.keepOpenOnSelect) {
      suppressNextAutoOpen.value = true;
      // Nested RAF: Headless UI schedules its own post-selection refocus
      // (sync or RAF). Our outer RAF gets queued first; nesting a second
      // RAF inside guarantees the blur/clear runs AFTER Headless UI's
      // refocus, so the suppress flag survives the intervening focus
      // event and the targeted blur isn't undone.
      cancelPostSelectRaf();
      postSelectRafId.value = requestAnimationFrame(() => {
        postSelectRafId.value = requestAnimationFrame(() => {
          postSelectRafId.value = null;
          suppressNextAutoOpen.value = false;
          (comboboxInput.value?.$el as HTMLElement | undefined)?.blur();
        });
      });
    }

    if (props.keepOpenOnSelect && comboboxButton.value?.$el) {
      // Re-open the dropdown after selection
      setTimeout(() => {
        if (comboboxButton.value?.$el) {
          comboboxButton.value.$el.click();
        }
        if (props.clearOnFocus) {
          searchQuery.value = "";
        }
      }, 50);
    }
  },
});

const displayValueFn = computed(() => {
  return (item: any): string => {
    if (item && typeof item === "object" && "label" in item) {
      return (item as Option).label;
    }
    return searchQuery.value;
  };
});

const normalizedSearchTerms = computed(() => {
  if (!searchQuery.value) {
    return [];
  }
  return searchQuery.value
    .trim()
    .split(/\s+/)
    .filter((term) => term.length > 0);
});

const filteredOptions = computed(() => {
  if (normalizedSearchTerms.value.length === 0) {
    return props.options;
  }

  // For each option, check if ALL search terms match somewhere in label or description
  return props.options.filter((option) => {
    const searchableText =
      `${option.label} ${option.description ?? ""}`.toLowerCase();
    return normalizedSearchTerms.value.every((term) =>
      searchableText.includes(term.toLowerCase())
    );
  });
});

const visibleOptions = computed(() =>
  filteredOptions.value.slice(0, MAX_RENDERED_OPTIONS)
);

const hiddenMatchCount = computed(
  () => filteredOptions.value.length - visibleOptions.value.length
);

const highlightQuery = computed(() => {
  if (normalizedSearchTerms.value.length === 0) {
    return "";
  }

  // Escape special regex characters and join terms with | for OR matching
  const escapedTerms = normalizedSearchTerms.value.map((term) =>
    term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  );
  return new RegExp(escapedTerms.join("|"), "gi");
});

const handleInputChange = (event: Event) => {
  if (props.disabled) {
    return;
  }
  if (event.target instanceof HTMLInputElement) {
    searchQuery.value = event.target.value;
    emit("queryUpdated", searchQuery.value);
  }
};

const handleInputFocus = (event: FocusEvent) => {
  if (props.disabled) {
    return;
  }
  if (props.clearOnFocus && event.target instanceof HTMLInputElement) {
    if (searchQuery.value !== "") {
      searchQuery.value = "";
      emit("queryUpdated", searchQuery.value);
    }
  }

  if (!props.autoOpen) {
    return;
  }
  if (suppressNextAutoOpen.value) {
    suppressNextAutoOpen.value = false;
    return;
  }
  // Programmatically click the ComboboxButton to open the dropdown
  if (
    comboboxButton.value &&
    comboboxButton.value.$el &&
    event.target instanceof HTMLInputElement
  ) {
    comboboxButton.value.$el.click();
  }
};

const handleComboboxButtonClick = () => {
  if (props.disabled) {
    return;
  }
  if (props.clearOnFocus && searchQuery.value !== "") {
    searchQuery.value = "";
    emit("queryUpdated", searchQuery.value);
    comboboxButton.value.$el.click();
  }
};

// Keeps the input in step with a selection made from outside (programmatic set, or a parent that
// resets the field), which the Combobox itself never sees.
watch(
  () => props.modelValue,
  (newValue) => {
    const selectedLabel = props.options.find(
      (option) => option.value === newValue
    )?.label;
    if (selectedLabel !== undefined) {
      searchQuery.value = selectedLabel;
      return;
    }
    if (props.allowFreeText) {
      // The model holds the query itself here, so mirroring it keeps filtering and highlighting in
      // step with the field. The query the parent feeds straight back after each keystroke is
      // already equal, so typing is untouched.
      searchQuery.value = typeof newValue === "string" ? newValue : "";
      return;
    }
    // Elsewhere the model holds an id or a code the options may not cover yet. Blanking the query
    // for such a value would erase what the user is typing, and with it the filtering.
    if (!newValue) {
      searchQuery.value = "";
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
// Bootstrap greys out a disabled .form-control but gives .input-group-text no disabled state of
// its own, so the trigger would stay fully lit next to a dimmed input.
.input-group-text:disabled {
  opacity: 0.65;
  cursor: default;
}

.search-result-item {
  position: relative;
  z-index: 1;
}
</style>
